import React, { useState, useRef, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import { Document, Packer, Paragraph, TextRun } from 'docx';
import { saveAs } from 'file-saver';
import mammoth from 'mammoth';
import ImavenLoadingScreen from '../../Helpers/loading-screen';
import { useToast } from '../../../Context/ToastContext';
import { useConfirm } from '../../../Context/ConfirmContext';

// Import styled components
import { EditorContainer, Editor, Toolbar } from './EditorStyles';

// Import editor hooks
import { useEditorLogic } from './useEditorLogic';

// Import editor UI components
import {
  FileMenu,
  FontMenu,
  FontSizeMenu,
  FormatMenu,
  AlignmentMenu,
  ListMenu,
  ImageMenu,
  ColorMenu,
  UndoRedoGroup,
  RecreateButton,
  SaveStatusIndicator,
  EditorContextMenu
} from './EditorToolbar';

// Import dialog components
import {
  SaveFileDialog,
  OpenFileDialog,
  ShareDialog,
  GeneratingOverlayDialog,
  RecreateDialog
} from './EditorDialogs';

const TextEditor = ({ threadId }) => {
  const { showToast } = useToast();
  const { confirm } = useConfirm();
  
  // State for UI components
  const [isFileMenuOpen, setIsFileMenuOpen] = useState(false);
  const [isFontOpen, setIsFontOpen] = useState(false);
  const [isSizeOpen, setIsSizeOpen] = useState(false);
  const [isImageMenuOpen, setIsImageMenuOpen] = useState(false);
  const [isFormatMenuOpen, setIsFormatMenuOpen] = useState(false);
  const [isAlignMenuOpen, setIsAlignMenuOpen] = useState(false);
  const [isListMenuOpen, setIsListMenuOpen] = useState(false);
  const [isColorMenuOpen, setIsColorMenuOpen] = useState(false);
  
  // State for dialogs
  const [isSaveDialogOpen, setIsSaveDialogOpen] = useState(false);
  const [isOpenDialogOpen, setIsOpenDialogOpen] = useState(false);
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [isRecreateDialogOpen, setIsRecreateDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  
  // State for saving/sharing
  const [saveFormat, setSaveFormat] = useState('txt');
  const [fileName, setFileName] = useState('document');
  const [shareLink, setShareLink] = useState('');
  const [shareOption, setShareOption] = useState('link');
  
  // Refs
  const fileInputRef = useRef(null);
  const openFileInputRef = useRef(null);
  const shareLinkRef = useRef(null);
  
  // Get editor logic from custom hook
  const editorLogic = useEditorLogic(threadId);
  const {
    editorRef,
    content,
    currentColor,
    currentHighlight,
    saveStatus,
    hasStartedEditing,
    isLoading,
    selectedImage,
    contextMenu,
    
    execCommand,
    handleInput,
    handleUndo,
    handleRedo,
    toggleFormat,
    handleFontSize,
    handleColorClick,
    handleHighlightClick,
    handleLink,
    handleMouseUp,
    handleImageClick,
    handleContextMenu,
    handleContextMenuAction,
    getConversationSummary,
    setContextMenu,
    setSaveStatus
  } = editorLogic;

  // Handle file actions (Save, Open, etc.)
  const handleFileAction = async (action) => {
    switch (action) {
      case 'new':
        const confirmed = await confirm({
          title: 'New Document',
          message: 'Create new document? Any unsaved changes will be lost.',
          confirmText: 'Create New',
          cancelText: 'Cancel',
          type: 'warning'
        });
        if (confirmed) {
          editorRef.current.innerHTML = '';
        }
        break;
      case 'open':
        // Open file dialog
        setIsOpenDialogOpen(true);
        break;
      case 'save':
        // Open save dialog with default filename
        setFileName(fileName || 'document');
        setIsSaveDialogOpen(true);
        break;
      case 'saveAs':
        // Open save dialog and prompt for new filename
        setFileName('');
        setIsSaveDialogOpen(true);
        break;
      case 'print':
        // Create a new window with just the editor content
        const printContent = editorRef.current.innerHTML;
        const printWindow = window.open('', '_blank');

        printWindow.document.write(`
          <!DOCTYPE html>
          <html>
          <head>
            <title>Print Document</title>
            <style>
              body {
                font-family: Arial, sans-serif;
                margin: 20px;
                color: #000;
                background: #fff;
              }
              img {
                max-width: 100%;
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
          </html>
        `);

        printWindow.document.close();

        // Wait for content to load before printing
        printWindow.onload = function () {
          printWindow.focus();
          printWindow.print();
          // Close the window after printing (or if print is canceled)
          printWindow.onafterprint = function () {
            printWindow.close();
          };
        };
        break;
      case 'share':
        // Open share dialog
        handleShare();
        break;
      default:
        // Default case - do nothing
        console.log(`Unhandled file action: ${action}`);
    }
    setIsFileMenuOpen(false);
  };

  // Function to handle sharing
  const handleShare = () => {
    // Get the document content
    const content = editorRef.current.innerHTML;
    const textContent = editorRef.current.innerText;
    const documentTitle = fileName || 'Untitled Document';

    // First create a downloadable file with full content that can be attached
    const blob = new Blob([textContent], { type: 'text/plain' });
    const fullContentFile = URL.createObjectURL(blob);
    const tempLink = document.createElement('a');
    tempLink.href = fullContentFile;
    tempLink.download = `${documentTitle}.txt`;
    document.body.appendChild(tempLink);
    tempLink.click();
    document.body.removeChild(tempLink);
    try {
      URL.revokeObjectURL(fullContentFile);
    } catch (e) {
      // noop
    }
    
    // Show a notification about the downloaded file
    setSaveStatus('Full document saved as .txt file for sharing. Please attach this file to your email.');
    setTimeout(() => setSaveStatus(''), 5000);

    // Check if Web Share API is supported
    if (navigator.share) {
      // Use a shorter preview for the share API
      const shortPreview = textContent.length > 500 ? 
        textContent.substring(0, 500) + '...\n\n[Full document has been downloaded as a .txt file. Please attach it to your email.]' : 
        textContent;
        
      navigator.share({
        title: documentTitle,
        text: shortPreview,
      })
        .then(() => {
          setSaveStatus('Shared successfully. Please attach the downloaded .txt file for the full content.');
          setTimeout(() => setSaveStatus(''), 5000);
        })
        .catch((error) => {
          console.error('Error sharing:', error);
          setSaveStatus('Sharing failed');
          setTimeout(() => setSaveStatus(''), 2000);
          // Fall back to the share dialog if sharing fails
          setIsShareDialogOpen(true);
        });
    } else {
      // Web Share API not supported, show our custom share dialog
      setIsShareDialogOpen(true);
    }
  };

  // Function to share via email - revised for better mail app compatibility
  const shareViaEmail = () => {
    const subject = encodeURIComponent(`Shared Document: ${fileName || 'Document'}`);
    const textContent = editorRef.current.innerText;
    
    // Create a preview for the email body - keep it short to work with most mail clients
    const emailPreview = textContent.length > 300 ? 
      textContent.substring(0, 300) + '...' : 
      textContent;
    
    // Prepare email body with clear instructions about the attachment
    const body = encodeURIComponent(
      `I'd like to share this document with you.\n\n` +
      `PREVIEW (partial content):\n${emailPreview}\n\n` +
      `The complete document has been saved as a text file. Please find it attached.\n` +
      `${shareLink ? `View online: ${shareLink}` : ''}`
    );
    
    // Open email client with limited content
    window.open(`mailto:?subject=${subject}&body=${body}`);
    setIsShareDialogOpen(false);
    
    // Remind the user about the attachment
    showToast("Please remember to attach the previously downloaded .txt file to your email to share the complete content.", "info");
  };

  // Function to copy share link to clipboard
  const copyShareLink = () => {
    if (shareLinkRef.current) {
      shareLinkRef.current.select();
      document.execCommand('copy');
      setSaveStatus('Link copied to clipboard');
      setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  // Function to handle file opening
  const handleFileOpen = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Update the filename
    setFileName(file.name.split('.')[0]);

    const reader = new FileReader();
    const fileExtension = file.name.split('.').pop().toLowerCase();

    // Set up reader onload event
    reader.onload = async (e) => {
      const fileContent = e.target.result;

      try {
        switch (fileExtension) {
          case 'txt':
            // Plain text - just insert the content
            editorRef.current.innerHTML = fileContent
              .replace(/\n/g, '<br>')
              .replace(/\t/g, '&nbsp;&nbsp;&nbsp;&nbsp;')
              .replace(/ {2}/g, '&nbsp;&nbsp;');
            setSaveStatus('Text file opened');
            setTimeout(() => setSaveStatus(''), 2000);
            break;

          case 'html':
          case 'htm':
            // HTML - parse and extract the body content
            const parser = new DOMParser();
            const doc = parser.parseFromString(fileContent, 'text/html');
            const bodyContent = doc.body.innerHTML;
            editorRef.current.innerHTML = bodyContent;
            setSaveStatus('HTML file opened');
            setTimeout(() => setSaveStatus(''), 2000);
            break;

          case 'docx':
            // For DOCX files, we need to use mammoth.js to convert to HTML
            setSaveStatus('Converting Word document...');
            try {
              const result = await mammoth.convertToHtml({ arrayBuffer: fileContent });
              editorRef.current.innerHTML = result.value;
              setSaveStatus('Word document opened');
              setTimeout(() => setSaveStatus(''), 2000);
            } catch (error) {
              console.error('Error opening Word document:', error);
              setSaveStatus('Error opening Word document');
              setTimeout(() => setSaveStatus(''), 2000);
              showToast('Could not open the Word document. The format may not be supported.', 'error');
            }
            break;

          default:
            showToast('Unsupported file format. Please use .txt, .html, or .docx files.', 'warning');
        }
      } catch (error) {
        console.error('Error opening file:', error);
        showToast('Error opening file. The file may be corrupted or in an unsupported format.', 'error');
      }
    };

    // Read the file based on its type
    if (fileExtension === 'txt' || fileExtension === 'html' || fileExtension === 'htm') {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }

    // Close the dialog
    setIsOpenDialogOpen(false);

    // Reset the file input
    event.target.value = '';
  };

  // Function to handle saving the document in different formats
  const saveDocument = () => {
    if (!fileName) {
      showToast('Please enter a file name', 'warning');
      return;
    }

    const content = editorRef.current.innerHTML;
    const textContent = editorRef.current.innerText;

    // Create a blob based on the selected format
    let blob;
    let fileExtension;

    switch (saveFormat) {
      case 'txt':
        blob = new Blob([textContent], { type: 'text/plain' });
        fileExtension = '.txt';

        // Create download link
        const txtUrl = URL.createObjectURL(blob);
        const txtLink = document.createElement('a');
        txtLink.href = txtUrl;
        txtLink.download = `${fileName}${fileExtension}`;
        document.body.appendChild(txtLink);
        txtLink.click();
        document.body.removeChild(txtLink);
        URL.revokeObjectURL(txtUrl);

        setIsSaveDialogOpen(false);
        setSaveStatus(`Saved as ${fileName}${fileExtension}`);
        setTimeout(() => setSaveStatus(''), 2000);
        break;

      case 'html':
        // Create the same beautiful styling as the PDF export
        const titleMatchHtml = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
        const titleHtml = titleMatchHtml ? titleMatchHtml[1] : "iMavenai Document";
        
        // Process content using the same function as for PDF
        const tempContainer = document.createElement('div');
        tempContainer.innerHTML = content;
        
        // Create a styled HTML document
        const htmlContent = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${fileName}</title>
  <style>
    * {
      margin: 0;
      padding: 0;
      box-sizing: border-box;
    }
    
    body {
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      line-height: 1.6;
      color: #e0e0e0;
      background: #121212;
      padding: 30px;
      min-height: 100vh;
    }
    
    .container {
      max-width: 900px;
      margin: 0 auto;
      background: #1a1a1a;
      border-radius: 16px;
      box-shadow: 0 10px 30px rgba(0,0,0,0.3);
      overflow: hidden;
    }
    
    .header {
      background: linear-gradient(135deg, #1e3a8a 0%, #3b0764 100%);
      color: white;
      padding: 40px 40px 30px;
      position: relative;
    }
    
    .header-border {
      height: 4px;
      background: linear-gradient(90deg, #f43f5e, #8b5cf6, #0ea5e9);
      width: 100%;
      margin-top: 10px;
    }
    
    .header h1 {
      font-size: 2.5rem;
      font-weight: 700;
      margin-bottom: 10px;
      color: white;
    }
    
    .content {
      padding: 40px;
    }
    
    .section {
      margin-bottom: 40px;
      border-left: 4px solid #6366f1;
      padding-left: 25px;
    }
    
    .section h1, .section h2 {
      color: #6366f1;
      margin-bottom: 20px;
      font-weight: 600;
    }
    
    .section h1 {
      font-size: 2rem;
    }
    
    .section h2 {
      font-size: 1.8rem;
    }
    
    .subsection {
      background: #252525;
      border-radius: 12px;
      padding: 25px;
      margin-bottom: 20px;
      border: 1px solid #333;
    }
    
    .subsection h3, .subsection h4, .subsection h5, .subsection h6 {
      color: #93c5fd;
      font-size: 1.3rem;
      margin-bottom: 15px;
      font-weight: 600;
    }
    
    p {
      margin-bottom: 16px;
      line-height: 1.7;
    }
    
    ul, ol {
      padding-left: 25px;
      margin-bottom: 20px;
    }
    
    li {
      margin-bottom: 8px;
    }
    
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 20px;
      border: 1px solid #333;
    }
    
    th, td {
      padding: 12px;
      border: 1px solid #333;
    }
    
    th {
      background: #252525;
      font-weight: 600;
    }
    
    blockquote {
      background: #252525;
      border-radius: 12px;
      padding: 25px;
      border-left: 4px solid #4f46e5;
      margin-bottom: 20px;
      font-style: italic;
    }
    
    img {
      max-width: 100%;
      border-radius: 8px;
      margin-bottom: 20px;
    }
    
    .footer {
      background: #1e1e1e;
      padding: 30px 40px;
      border-top: 1px solid rgba(255, 255, 255, 0.1);
      text-align: center;
      color: #9ca3af;
      font-style: italic;
    }
    
    @media (max-width: 768px) {
      body {
        padding: 10px;
      }
      
      .header {
        padding: 30px 20px 20px;
      }
      
      .header h1 {
        font-size: 2rem;
      }
      
      .content {
        padding: 20px;
      }
    }
  </style>
</head>
<body>
  <div class="container">
    <div class="header">
      <h1>${titleHtml}</h1>
      <div class="header-border"></div>
    </div>
    
    <div class="content">
      ${processHTMLContent(tempContainer)}
    </div>
    
    <div class="footer">
      Generated with iMavenai. This document reflects the content created in the iMavenai platform.
    </div>
  </div>
</body>
</html>`;

        // Function to process the HTML content and wrap sections appropriately
        function processHTMLContent(source) {
          let processedHTML = '';
          let inSection = false;
          let inSubsection = false;
          
          // Convert the DOM nodes to a string with appropriate wrappers
          const nodes = Array.from(source.childNodes);
          
          nodes.forEach(node => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Create new sections for headings
              const nodeHTML = node.outerHTML;
              
              if (node.tagName.match(/^H[1-2]$/i)) {
                // Close previous section if open
                if (inSection) {
                  processedHTML += '</div>';
                }
                
                // Start a new section
                processedHTML += `<div class="section">`;
                processedHTML += nodeHTML;
                inSection = true;
                inSubsection = false;
              } 
              else if (node.tagName.match(/^H[3-6]$/i)) {
                // Close previous subsection if open
                if (inSubsection) {
                  processedHTML += '</div>';
                }
                
                // Make sure we're in a section
                if (!inSection) {
                  processedHTML += `<div class="section">`;
                  inSection = true;
                }
                
                // Start a new subsection
                processedHTML += `<div class="subsection">`;
                processedHTML += nodeHTML;
                inSubsection = true;
              }
              else {
                // Regular content
                if (inSubsection) {
                  // Add to current subsection
                  processedHTML += nodeHTML;
                } else if (inSection) {
                  // Add to current section
                  processedHTML += nodeHTML;
                } else {
                  // Not in a section yet, create one
                  processedHTML += `<div class="section">`;
                  processedHTML += nodeHTML;
                  inSection = true;
                }
              }
            }
          });
          
          // Close any open sections
          if (inSubsection) {
            processedHTML += '</div>'; // Close subsection
          }
          
          if (inSection) {
            processedHTML += '</div>'; // Close section
          }
          
          return processedHTML;
        }
        
        blob = new Blob([htmlContent], { type: 'text/html' });
        fileExtension = '.html';

        // Create download link
        const htmlUrl = URL.createObjectURL(blob);
        const htmlLink = document.createElement('a');
        htmlLink.href = htmlUrl;
        htmlLink.download = `${fileName}${fileExtension}`;
        document.body.appendChild(htmlLink);
        htmlLink.click();
        document.body.removeChild(htmlLink);
        URL.revokeObjectURL(htmlUrl);

        setIsSaveDialogOpen(false);
        setSaveStatus(`Saved as ${fileName}${fileExtension}`);
        setTimeout(() => setSaveStatus(''), 2000);
        break;

      case 'pdf':
        // For PDF export using html2pdf library
        setIsGenerating(true);
        setSaveStatus('Generating PDF...');
        setIsSaveDialogOpen(false);

        const element = document.createElement('div');
        element.innerHTML = content;
        
        // Create a professional styled wrapper
        const wrapper = document.createElement('div');
        wrapper.style.fontFamily = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif";
        wrapper.style.lineHeight = "1.6";
        wrapper.style.background = "#121212"; // Dark background
        wrapper.style.padding = "30px";
        wrapper.style.color = "#e0e0e0";
        
        // Create container with nice styling
        const container = document.createElement('div');
        container.style.maxWidth = "900px";
        container.style.margin = "0 auto";
        container.style.background = "#1a1a1a"; // Dark theme container
        container.style.borderRadius = "16px";
        container.style.boxShadow = "0 10px 30px rgba(0,0,0,0.3)";
        container.style.overflow = "hidden";
        
        // Create header with gradient
        const header = document.createElement('div');
        header.style.background = "linear-gradient(135deg, #1e3a8a 0%, #3b0764 100%)"; // Dark blue to purple gradient
        header.style.color = "white";
        header.style.padding = "40px 40px 30px";
        header.style.position = "relative";
        
        // Add colorful border under header
        const headerAfter = document.createElement('div');
        headerAfter.style.height = "4px";
        headerAfter.style.background = "linear-gradient(90deg, #f43f5e, #8b5cf6, #0ea5e9)";
        headerAfter.style.width = "100%";
        
        // Extract title from content or use default
        const titleMatch = content.match(/<h1[^>]*>(.*?)<\/h1>/i);
        const title = titleMatch ? titleMatch[1] : "Maven.ai Document";
        
        // Create title element
        const titleElement = document.createElement('h1');
        titleElement.innerHTML = title;
        titleElement.style.fontSize = "2.5rem";
        titleElement.style.fontWeight = "700";
        titleElement.style.marginBottom = "10px";
        titleElement.style.color = "white";
        
        // Add elements to header
        header.appendChild(titleElement);
        header.appendChild(headerAfter);
        
        // Create content container
        const contentContainer = document.createElement('div');
        contentContainer.style.padding = "40px";
        
        // Process the content to create styled sections
        const processedContent = processContentIntoSections(element);
        contentContainer.appendChild(processedContent);
        
        // Add footer
        const footer = document.createElement('div');
        footer.style.background = "#1e1e1e";
        footer.style.padding = "30px 40px";
        footer.style.borderTop = "1px solid rgba(255, 255, 255, 0.1)";
        footer.style.textAlign = "center";
        footer.style.color = "#9ca3af";
        footer.style.fontStyle = "italic";
        footer.innerHTML = "Generated with iMavenai. This document reflects the content created in the iMavenai platform.";
        
        // Assemble the final document
        container.appendChild(header);
        container.appendChild(contentContainer);
        container.appendChild(footer);
        wrapper.appendChild(container);
        
        // Function to process content into styled sections
        function processContentIntoSections(source) {
          const fragment = document.createDocumentFragment();
          
          // Function to create styled headings
          const createStyledHeading = (element) => {
            const level = parseInt(element.tagName.substring(1), 10);
            const section = document.createElement('div');
            section.style.marginBottom = "40px";
            
            if (level <= 2) {
              section.style.borderLeft = "4px solid #6366f1"; // Indigo color
              section.style.paddingLeft = "25px";
              
              const heading = document.createElement(element.tagName);
              heading.innerHTML = element.innerHTML;
              heading.style.color = "#6366f1"; // Indigo for headings
              heading.style.fontSize = level === 1 ? "2rem" : "1.8rem";
              heading.style.marginBottom = "20px";
              heading.style.fontWeight = "600";
              
              section.appendChild(heading);
              return section;
            } else {
              const subsection = document.createElement('div');
              subsection.style.background = "#252525";
              subsection.style.borderRadius = "12px";
              subsection.style.padding = "25px";
              subsection.style.marginBottom = "20px";
              subsection.style.border = "1px solid #333";
              
              const heading = document.createElement(element.tagName);
              heading.innerHTML = element.innerHTML;
              heading.style.color = "#93c5fd"; // Light blue for subheadings
              heading.style.fontSize = "1.3rem";
              heading.style.marginBottom = "15px";
              heading.style.fontWeight = "600";
              
              subsection.appendChild(heading);
              section.appendChild(subsection);
              return section;
            }
          };
          
          // Process each top-level element
          const nodes = Array.from(source.childNodes);
          let currentSection = null;
          
          nodes.forEach(node => {
            // Remove any green background or left borders from original content
            if (node.nodeType === Node.ELEMENT_NODE) {
              // Remove any existing background colors - especially green ones
              if (node.style && (
                  node.style.backgroundColor === 'green' || 
                  node.style.backgroundColor === '#00ff00' || 
                  node.style.backgroundColor.includes('rgb(0, 128, 0)') ||
                  node.style.backgroundColor.includes('rgb(0, 255, 0)')
                )) {
                node.style.backgroundColor = '#252525';
              }
              
              // Remove any green borders
              if (node.style && (
                  node.style.borderLeftColor === 'green' || 
                  node.style.borderLeftColor === '#00ff00' ||
                  node.style.borderLeftColor.includes('rgb(0, 128, 0)') ||
                  node.style.borderLeftColor.includes('rgb(0, 255, 0)')
                )) {
                node.style.borderLeftColor = '#6366f1';
              }
              
              // Handle headings specially
              if (node.tagName.match(/^H[1-6]$/i)) {
                if (currentSection) {
                  fragment.appendChild(currentSection);
                }
                currentSection = createStyledHeading(node);
              } 
              // Handle paragraphs and other elements
              else {
                if (!currentSection) {
                  currentSection = document.createElement('div');
                  currentSection.style.marginBottom = "40px";
                }
                
                // Style paragraphs
                if (node.tagName === 'P') {
                  node.style.marginBottom = "16px";
                  node.style.lineHeight = "1.7";
                }
                
                // Style lists
                if (node.tagName === 'UL' || node.tagName === 'OL') {
                  node.style.paddingLeft = "25px";
                  node.style.marginBottom = "20px";
                  
                  // Style list items
                  Array.from(node.querySelectorAll('li')).forEach(li => {
                    li.style.marginBottom = "8px";
                  });
                }
                
                // Style tables
                if (node.tagName === 'TABLE') {
                  node.style.width = "100%";
                  node.style.borderCollapse = "collapse";
                  node.style.marginBottom = "20px";
                  node.style.border = "1px solid #333";
                  
                  // Style table cells
                  Array.from(node.querySelectorAll('th, td')).forEach(cell => {
                    cell.style.padding = "12px";
                    cell.style.border = "1px solid #333";
                    
                    if (cell.tagName === 'TH') {
                      cell.style.background = "#252525";
                      cell.style.fontWeight = "600";
                    }
                  });
                }
                
                // Style blockquotes
                if (node.tagName === 'BLOCKQUOTE') {
                  node.style.background = "#252525";
                  node.style.borderRadius = "12px";
                  node.style.padding = "25px";
                  node.style.borderLeft = "4px solid #4f46e5";
                  node.style.marginBottom = "20px";
                  node.style.fontStyle = "italic";
                }
                
                // Style images
                if (node.tagName === 'IMG') {
                  node.style.maxWidth = "100%";
                  node.style.borderRadius = "8px";
                  node.style.marginBottom = "20px";
                }
                
                // Clone and process the node to remove any green background
                const processedNode = node.cloneNode(true);
                
                // Process all elements inside this node to remove green backgrounds
                Array.from(processedNode.querySelectorAll('*')).forEach(el => {
                  // Remove green backgrounds
                  if (el.style && el.style.backgroundColor) {
                    if (el.style.backgroundColor === 'green' || 
                        el.style.backgroundColor === '#00ff00' || 
                        el.style.backgroundColor.includes('rgb(0, 128, 0)') ||
                        el.style.backgroundColor.includes('rgb(0, 255, 0)')) {
                      el.style.backgroundColor = '#252525';
                    }
                  }
                  
                  // Remove green borders
                  if (el.style && el.style.borderLeft) {
                    if (el.style.borderLeftColor === 'green' || 
                        el.style.borderLeftColor === '#00ff00' ||
                        el.style.borderLeftColor.includes('rgb(0, 128, 0)') ||
                        el.style.borderLeftColor.includes('rgb(0, 255, 0)')) {
                      el.style.borderLeftColor = '#6366f1';
                    }
                  }
                });
                
                currentSection.appendChild(processedNode);
              }
            }
          });
          
          if (currentSection) {
            fragment.appendChild(currentSection);
          }
          
          return fragment;
        }
        
        const opt = {
          margin: [20, 20],
          filename: `${fileName}.pdf`,
          image: { type: 'jpeg', quality: 0.98 },
          html2canvas: { 
            scale: 2, 
            useCORS: true, 
            backgroundColor: "#121212",
            letterRendering: true,
            logging: false
          },
          jsPDF: { 
            unit: 'mm', 
            format: [580, 2000], // Custom super-tall format to avoid page breaks
            orientation: 'portrait',
            compress: true,
            hotfixes: ['px_scaling'] // Fixes scaling issues
          },
          pagebreak: {
            mode: 'avoid-all' // Avoid all page breaks
          }
        };

        // Use html2pdf to generate and save the PDF
        html2pdf().from(wrapper).set(opt).save()
          .then(() => {
            setIsGenerating(false);
            setSaveStatus('PDF saved');
            setTimeout(() => setSaveStatus(''), 2000);
          })
          .catch(error => {
            console.error('Error generating PDF:', error);
            setIsGenerating(false);
            setSaveStatus('Error saving PDF');
          });
        break;

      case 'docx':
        // For Word export using docx library
        setIsGenerating(true);
        setSaveStatus('Generating Word document...');
        setIsSaveDialogOpen(false);

        try {
          // Create a simple HTML parser to extract text with basic formatting
          const parser = new DOMParser();
          const htmlDoc = parser.parseFromString(content, 'text/html');

          // Function to process HTML nodes and convert to docx paragraphs
          const processNodes = (nodes) => {
            const paragraphs = [];

            // Process each node
            nodes.forEach(node => {
              if (node.nodeType === Node.TEXT_NODE) {
                // Text node - add as simple text
                if (node.textContent.trim()) {
                  paragraphs.push(
                    new Paragraph({
                      children: [new TextRun(node.textContent)]
                    })
                  );
                }
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                // Element node - handle based on tag
                switch (node.tagName.toLowerCase()) {
                  case 'p':
                  case 'div':
                    // Process children of block elements
                    const childParagraphs = processNodes(Array.from(node.childNodes));
                    paragraphs.push(...childParagraphs);
                    break;

                  case 'br':
                    // Add empty paragraph for line breaks
                    paragraphs.push(new Paragraph({}));
                    break;

                  case 'strong':
                  case 'b':
                    // Bold text
                    paragraphs.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: node.textContent,
                            bold: true
                          })
                        ]
                      })
                    );
                    break;

                  case 'em':
                  case 'i':
                    // Italic text
                    paragraphs.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: node.textContent,
                            italic: true
                          })
                        ]
                      })
                    );
                    break;

                  case 'u':
                    // Underlined text
                    paragraphs.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: node.textContent,
                            underline: {}
                          })
                        ]
                      })
                    );
                    break;

                  case 'img':
                    // Images aren't directly supported in this simple implementation
                    // We add a placeholder text
                    paragraphs.push(
                      new Paragraph({
                        children: [
                          new TextRun({
                            text: "[Image]",
                            italics: true,
                            color: "808080"
                          })
                        ]
                      })
                    );
                    break;

                  default:
                    // For other elements, just get their text content
                    if (node.textContent.trim()) {
                      paragraphs.push(
                        new Paragraph({
                          children: [new TextRun(node.textContent)]
                        })
                      );
                    }
                }
              }
            });

            return paragraphs;
          };

          // Process the HTML content
          const bodyContent = htmlDoc.body || htmlDoc.documentElement;
          const docxParagraphs = processNodes(Array.from(bodyContent.childNodes));

          // Create a new document with the processed paragraphs
          const doc = new Document({
            sections: [{
              properties: {},
              children: docxParagraphs.length > 0 ? docxParagraphs : [new Paragraph({ text: textContent })]
            }]
          });

          // Generate the document as a blob
          Packer.toBlob(doc).then(blob => {
            saveAs(blob, `${fileName}.docx`);
            setIsGenerating(false);
            setSaveStatus('Word document saved');
            setTimeout(() => setSaveStatus(''), 2000);
          });
        } catch (error) {
          console.error('Error generating Word document:', error);
          setIsGenerating(false);
          setSaveStatus('Error saving Word document');
        }
        break;

      default:
        blob = new Blob([textContent], { type: 'text/plain' });
        fileExtension = '.txt';

        // Create download link
        const defaultUrl = URL.createObjectURL(blob);
        const defaultLink = document.createElement('a');
        defaultLink.href = defaultUrl;
        defaultLink.download = `${fileName}${fileExtension}`;
        document.body.appendChild(defaultLink);
        defaultLink.click();
        document.body.removeChild(defaultLink);
        URL.revokeObjectURL(defaultUrl);

        setIsSaveDialogOpen(false);
        setSaveStatus(`Saved as ${fileName}${fileExtension}`);
        setTimeout(() => setSaveStatus(''), 2000);
    }
  };

  const handleRefresh = () => {
    setIsRecreateDialogOpen(true);
  };

  const confirmRefresh = () => {
    getConversationSummary(true);
    setIsRecreateDialogOpen(false);
  };

  const handleImageFromUrl = () => {
    const url = prompt('Enter image URL:');
    if (url) {
      execCommand('insertImage', url);
      setIsImageMenuOpen(false);
    }
  };

  // Key event handling
  const handleKeyDown = (e) => {
    // Handle image deletion
    if (selectedImage && (e.key === 'Delete' || e.key === 'Backspace')) {
      e.preventDefault();
      selectedImage.remove();
      editorLogic.setSelectedImage(null);
      
      // Save state after deleting image
      const newContent = editorRef.current.innerHTML;
      editorLogic.setContent(newContent);
      editorLogic.saveToHistory(newContent);
    }

    // Handle tab key
    if (e.key === 'Tab') {
      e.preventDefault();
      // Track current cursor position
      editorLogic.trackExactCursorPosition();
      
      // Insert tab spaces
      document.execCommand('insertHTML', false, '&nbsp;&nbsp;&nbsp;&nbsp;');
      
      // Restore cursor position
      setTimeout(() => {
        editorLogic.restoreExactCursorPosition();
      }, 10);
    }

    // Enhanced keyboard shortcuts
    if (e.ctrlKey) {
      switch (e.key) {
        case 's':
          e.preventDefault();
          if (e.shiftKey) {
            // Ctrl+Shift+S for Save As
            handleFileAction('saveAs');
          } else {
            // Ctrl+S for Save
            handleFileAction('save');
          }
          break;
          
        case 'z':
          e.preventDefault();
          handleUndo();
          break;
          
        case 'y':
          e.preventDefault();
          handleRedo();
          break;
          
        case 'b':
          e.preventDefault();
          toggleFormat('bold');
          break;
          
        case 'i':
          e.preventDefault();
          toggleFormat('italic');
          break;
          
        case 'u':
          e.preventDefault();
          toggleFormat('underline');
          break;
          
        case 'l':
          e.preventDefault();
          handleLink();
          break;
          
        case 'p':
          e.preventDefault();
          handleFileAction('print');
          break;
          
        case 'o':
          e.preventDefault();
          handleFileAction('open');
          break;
          
        case 'n':
          e.preventDefault();
          handleFileAction('new');
          break;
          
        default:
          break;
      }
    }
  };

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.isOpen) {
        setContextMenu((prev) => ({ ...prev, isOpen: false }));
      }
    };
    
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.isOpen, setContextMenu]);

  return (
    <EditorContainer>
      {isLoading && <ImavenLoadingScreen />}
      
      <Toolbar>
        <FileMenu 
          isOpen={isFileMenuOpen} 
          setIsOpen={setIsFileMenuOpen}
          handleFileAction={handleFileAction}
        />
        
        <FontMenu
          isFontOpen={isFontOpen}
          setIsFontOpen={setIsFontOpen}
          execCommand={execCommand}
        />
        
        <FontSizeMenu
          isSizeOpen={isSizeOpen}
          setIsSizeOpen={setIsSizeOpen}
          handleFontSize={handleFontSize}
        />
        
        <FormatMenu
          isFormatMenuOpen={isFormatMenuOpen}
          setIsFormatMenuOpen={setIsFormatMenuOpen}
          toggleFormat={toggleFormat}
          execCommand={execCommand}
          handleLink={handleLink}
        />
        
        <AlignmentMenu
          isAlignMenuOpen={isAlignMenuOpen}
          setIsAlignMenuOpen={setIsAlignMenuOpen}
          execCommand={execCommand}
        />
        
        <ListMenu
          isListMenuOpen={isListMenuOpen}
          setIsListMenuOpen={setIsListMenuOpen}
          execCommand={execCommand}
        />
        
        <ImageMenu
          isImageMenuOpen={isImageMenuOpen}
          setIsImageMenuOpen={setIsImageMenuOpen}
          handleImageFromUrl={handleImageFromUrl}
          fileInputRef={fileInputRef}
        />
        
        <ColorMenu
          isColorMenuOpen={isColorMenuOpen}
          setIsColorMenuOpen={setIsColorMenuOpen}
          currentColor={currentColor}
          currentHighlight={currentHighlight}
          handleColorClick={handleColorClick}
          handleHighlightClick={handleHighlightClick}
        />
        
        <UndoRedoGroup
          handleUndo={handleUndo}
          handleRedo={handleRedo}
        />
        
        <RecreateButton handleRefresh={handleRefresh} />
        
        <SaveStatusIndicator
          saveStatus={saveStatus}
          hasStartedEditing={hasStartedEditing}
        />
      </Toolbar>

      {/* Editor Container */}
      <Editor
        ref={editorRef}
        contentEditable
        onInput={handleInput}
        onClick={handleImageClick}
        onContextMenu={handleContextMenu}
        onMouseUp={handleMouseUp}
        onKeyDown={handleKeyDown}
        dangerouslySetInnerHTML={{ __html: content }}
        onBlur={(e) => {
          // Only prevent default for toolbar interactions
          const relatedTarget = e.relatedTarget;
          if (relatedTarget && (
            relatedTarget.tagName === 'BUTTON' || 
            relatedTarget.closest('button') ||
            relatedTarget.closest('.Toolbar')
          )) {
            e.preventDefault();
          }
        }}
        onFocus={() => {
          // Restore cursor position on focus if it was due to a user operation
          if (editorLogic.cursorPosition && editorLogic.cursorPosition.isAfterUserOperation) {
            editorLogic.restoreExactCursorPosition();
          }
        }}
      />
      
      {/* Dialogs */}
      <SaveFileDialog
        isOpen={isSaveDialogOpen}
        fileName={fileName}
        setFileName={setFileName}
        saveFormat={saveFormat}
        setSaveFormat={setSaveFormat}
        onCancel={() => setIsSaveDialogOpen(false)}
        onSave={saveDocument}
      />
      
      <OpenFileDialog
        isOpen={isOpenDialogOpen}
        onCancel={() => setIsOpenDialogOpen(false)}
        onOpen={handleFileOpen}
        fileInputRef={openFileInputRef}
      />
      
      <ShareDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        shareLinkRef={shareLinkRef}
        shareLink={shareLink}
        copyShareLink={copyShareLink}
        shareViaEmail={shareViaEmail}
      />
      
      <RecreateDialog
        isOpen={isRecreateDialogOpen}
        onCancel={() => setIsRecreateDialogOpen(false)}
        onConfirm={confirmRefresh}
      />
      
      <GeneratingOverlayDialog 
        isVisible={isGenerating}
        message={saveStatus}
      />
      
      <EditorContextMenu 
        contextMenu={contextMenu}
        handleContextMenuAction={handleContextMenuAction}
      />
    </EditorContainer>
  );
};

export default TextEditor; 